import React, { useState } from 'react';
import { Package, RotateCcw, Trash2, Flag, AlertTriangle, Crosshair, X, ShieldAlert, FileText } from 'lucide-react';
import { useStashStore, type StashedElement, type StashReason } from '../../stores/useStashStore';
import { useGraphStore } from '../../stores/useGraphStore';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { canvasController, beginControlledWrite, endControlledWrite } from '../../services/canvasController';
import { safeStr } from '../../utils/safeStr';
import { markSuspicious } from '../../services/flagOps';
import { toast } from 'sonner';

/**
 * Stash Tray — unified drawer view of every element that has been removed
 * from the canvas but kept for restoration. Backed by `useStashStore`.
 *
 * Groups entries by their stash *reason* so the user sees "Hidden by me"
 * items separately from "Filtered out" items — they have different
 * restoration semantics. User can restore individual items or whole groups.
 */

interface StashTrayProps {
    /**
     * Optional: auto-restore callback when the "Restore all" button is
     * clicked for a reason-group. Defaults to canvasController.restoreByReason.
     * Useful if the parent wants to show a toast.
     */
    onRestoreAllReason?: (reason: StashReason) => void;
}

const REASON_LABEL: Record<StashReason, string> = {
    // user-hide and manual-stash are functionally the same ("user decided to hide this");
    // we share the label so the tray shows a single "Stashed by me" group.
    'user-hide': 'Stashed by me',
    'manual-stash': 'Stashed by me',
    'filter': 'Filtered out',
    'algorithm': 'Pruned by algorithm',
    'orphan': 'Orphan edges',
};

const REASON_ORDER: StashReason[] = ['user-hide', 'manual-stash', 'filter', 'algorithm', 'orphan'];

// 'auto' = backend triage chokepoint flagged the node (high_risk /
// sanctioned / mixer_entity). 'flagged' = user clicked Flag from the
// canvas right-click menu. 'suspicious' = user clicked Flag in the
// Inspector ActionsTab. All three render the same red flag SVG on the
// canvas (slot 1 of bg-image); the tray distinguishes them visually so
// the officer can quickly see WHICH addresses were flagged automatically
// vs by their own action.
type MarkerKind = 'auto' | 'flagged' | 'suspicious';
interface MarkedNode {
    id: string;
    kind: MarkerKind;
    entity_name?: string;
    risk_score?: number;
    /** Human-readable reasons from triage payload (auto-flag only). */
    flag_reasons?: string[];
}

// C5/F4 — friendly labels for the report kinds stamped by markNodeReported.
const REPORT_TYPE_LABEL: Record<string, string> = {
    auto_investigate: 'Targeted Node Intelligence',
    follow_the_money: 'Follow the Money',
    holistic: 'Holistic Multi-Node Trace',
    ai_graph_summary: 'AI Graph Summary',
};

export const StashTray: React.FC<StashTrayProps> = ({ onRestoreAllReason }) => {
    const [expanded, setExpanded] = useState(false);
    const entries = useStashStore(s => s.entries);
    const elements = useGraphStore(s => s.elements);

    const entryList = React.useMemo(() => Object.values(entries), [entries]);

    // Items that stay on canvas but are flagged. Three kinds: auto-flagged
    // by the backend triage chokepoint, manually flagged via the canvas
    // right-click menu (flagged_for_review), and Inspector-flagged
    // (manually_flagged). We show all three in the tray so the user has a
    // single place to see every flag in the investigation.
    //
    // Cascade: manually_flagged > auto > review — mirrors the canvas
    // getFlagUri cascade so the badge and the tray section never
    // disagree. (Step 9j.3 escalation strips the source tag on promote,
    // so a clean state should never carry two flag tags simultaneously.
    // The cascade is a safety-net for transient/imported state.)
    const markedNodes = React.useMemo<MarkedNode[]>(() => {
        const out: MarkedNode[] = [];
        for (const el of elements) {
            if (el.data?.source) continue; // nodes only
            const tags: string[] = Array.isArray(el.data?.entity_tags) ? el.data.entity_tags : [];
            const reasons: string[] = Array.isArray(el.data?.flag_reasons) ? el.data.flag_reasons : [];
            const base = { id: el.data.id, entity_name: el.data.entity_name, risk_score: el.data.risk_score };
            if (tags.includes('manually_flagged')) {
                out.push({ ...base, kind: 'suspicious' });
            } else if (tags.includes('auto_flagged')) {
                out.push({ ...base, kind: 'auto', flag_reasons: reasons });
            } else if (tags.includes('flagged_for_review')) {
                out.push({ ...base, kind: 'flagged' });
            }
        }
        return out;
    }, [elements]);

    // C5/F4 — nodes that have had a report generated for them. A pure projection
    // over the same `report_types` flag the canvas badge reads (one flag, two
    // surfaces). Friendly labels for the report kinds.
    const reportedNodes = React.useMemo(() => {
        const out: Array<{ id: string; entity_name?: string; report_types: string[] }> = [];
        for (const el of elements) {
            if (el.data?.source) continue; // nodes only
            const rt: string[] = Array.isArray(el.data?.report_types) ? el.data.report_types : [];
            if (rt.length > 0) out.push({ id: el.data.id, entity_name: el.data.entity_name, report_types: rt });
        }
        return out;
    }, [elements]);

    const total = entryList.length + markedNodes.length + reportedNodes.length;
    if (total === 0) return null;

    // Group by display label so 'user-hide' and 'manual-stash' collapse into
    // a single "Stashed by me" section. The underlying reasons remain distinct
    // in the store for restore-by-reason actions.
    type DisplayGroup = { label: string; reasons: StashReason[]; items: StashedElement[] };
    const displayGroups: DisplayGroup[] = [];
    const groupByLabel = new Map<string, DisplayGroup>();
    for (const reason of REASON_ORDER) {
        const label = REASON_LABEL[reason];
        let group = groupByLabel.get(label);
        if (!group) {
            group = { label, reasons: [], items: [] };
            groupByLabel.set(label, group);
            displayGroups.push(group);
        }
        group.reasons.push(reason);
    }
    for (const entry of entryList) {
        const label = REASON_LABEL[entry.reason];
        const group = groupByLabel.get(label);
        if (group) group.items.push(entry);
    }

    const formatAddress = (id: string) => {
        if (!id || id.length < 12) return id;
        return `${id.slice(0, 6)}…${id.slice(-4)}`;
    };
    const formatTime = (ts: number) => {
        const d = new Date(ts);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const restoreOne = (id: string) => canvasController.restore([id]);
    const restoreGroup = (group: DisplayGroup) => {
        for (const reason of group.reasons) {
            if (onRestoreAllReason) onRestoreAllReason(reason);
            else canvasController.restoreByReason(reason);
        }
    };
    const restoreEverything = () => {
        for (const group of displayGroups) {
            if (group.items.length > 0) restoreGroup(group);
        }
    };
    // A4: route clearAll through canvasController.remove so it pushes an undo
    // snapshot and clears BOTH canvas + stash (undo parity), instead of nuking
    // only the stash store with no snapshot.
    const clearAll = () => canvasController.remove(Object.keys(useStashStore.getState().entries));

    // Locate = select the node on canvas AND in the selection store.
    //   - Store selection drives Node Inspector.
    //   - Cytoscape selection draws the highlight ring and triggers any
    //     cy-level listeners.
    //   - Gentle center animation helps the user actually spot it.
    const locateNode = (id: string) => {
        const el = useGraphStore.getState().elements.find(e => e.data?.id === id && !e.data?.source);
        if (!el) return;
        useSelectionStore.getState().selectNode({ ...(el.data as any) });
        const cy = (window as any).__cy;
        if (cy) {
            const node = cy.getElementById(id);
            if (node && node.length > 0) {
                cy.elements().unselect();
                node.select();
                cy.animate({ center: { eles: node } } as any, { duration: 300 } as any);
            }
        }
    };

    // Dismiss a flag from a node — the row leaves the tray, but the node
    // stays on canvas (just without the section-specific tag).
    //
    // §9j.3 changed this to PER-SECTION: dismissing an 'auto' row only
    // strips 'auto_flagged' (not all flag tags). This matters because the
    // 9j.2 cascade allows multiple flag tags on one node (review + auto +
    // manual coexisting); if the user clicks Dismiss on the auto row, they
    // expect the auto-flag to clear but a manual flag (if present) to stay.
    //
    // Caveat carried over from the original: dismissing an auto-flag does
    // NOT touch the underlying risk_score / sanctions / mixer data. The
    // backend will re-add 'auto_flagged' on next triage if conditions
    // still hold — that's intentional. Dismissing the FLAG isn't
    // dismissing the DATA.
    const unmark = (id: string, kind: MarkerKind) => {
        const tagToRemove = kind === 'auto'
            ? 'auto_flagged'
            : kind === 'flagged'
                ? 'flagged_for_review'
                : 'manually_flagged';
        // Labels are decorative — backend stamps '🚩 Flagged' for both
        // auto and review. Stripping by tag is the source-of-truth; we
        // strip the section-specific label too but leave shared ones.
        const labelToRemove = kind === 'auto'
            ? '🚩 Auto-flagged'
            : kind === 'flagged'
                ? '🚩 Flagged'
                : '⚠ Suspicious';
        const graph = useGraphStore.getState();
        const next = graph.elements.map(el => {
            if (el.data?.id !== id || el.data?.source) return el;
            const tags: string[] = Array.isArray(el.data.entity_tags) ? el.data.entity_tags : [];
            const labels: string[] = Array.isArray(el.data.node_labels) ? el.data.node_labels : [];
            return {
                ...el,
                data: {
                    ...el.data,
                    entity_tags: tags.filter(t => t !== tagToRemove),
                    node_labels: labels.filter(l => l !== labelToRemove),
                },
            };
        });
        beginControlledWrite();
        try { graph.setElements(next); } finally { endControlledWrite(); }
    };

    // §9j.3 — Promote a review/auto row to the manually_flagged (CONFIRMED,
    // solid red) state in ONE click. Now THE same action as the Inspector
    // ActionsTab's onEscalateToSuspicious and the right-click "Mark Suspicious":
    // all route through flagOps.markSuspicious (R-SSOT-7), which strips existing
    // flag tags/labels, adds manually_flagged + '⚠ Suspicious', raises risk to
    // 0.85, and persists best-effort.
    const confirmSuspicious = async (id: string) => {
        // R-SSOT-7: same canonical flagOps.markSuspicious as the Inspector +
        // right-click. (Was a 4th copy of the escalate logic.)
        const res = await markSuspicious(id);
        toast.success(res === 'saved'
            ? 'Promoted to suspicious (saved)'
            : 'Promoted to suspicious (local only)');
    };

    return (
        <div className="absolute top-0 bottom-0 left-0 z-20 flex">
            {/* Drawer Panel */}
            <div
                className={`h-full border-r border-border transition-all duration-300 overflow-hidden flex flex-col bg-card ${expanded ? 'w-80' : 'w-0 border-r-0'}`}
            >
                <div className="flex items-center justify-between p-4 border-b border-border/60 shrink-0">
                    <div className="flex items-center gap-2 text-foreground">
                        <Package className="w-4 h-4 text-amber-500" />
                        <span className="font-semibold text-sm">Tray</span>
                    </div>
                    <span className="px-2 py-0.5 bg-amber-500/10 text-amber-500 text-[10px] rounded-full font-bold tracking-wider">{total}</span>
                </div>

                <div className="flex-1 overflow-y-auto p-2">
                    {/* Marked nodes (auto-flagged / flagged / suspicious) — stay
                        on canvas but surfaced here so the officer has a single
                        list of every flagged address in the investigation. */}
                    {markedNodes.length > 0 && (() => {
                        const auto = markedNodes.filter(m => m.kind === 'auto');
                        const flagged = markedNodes.filter(m => m.kind === 'flagged');
                        const suspicious = markedNodes.filter(m => m.kind === 'suspicious');
                        const renderSection = (items: MarkedNode[], kind: MarkerKind) => {
                            if (items.length === 0) return null;
                            const Icon = kind === 'auto' || kind === 'flagged' ? Flag : AlertTriangle;
                            const iconCls = kind === 'auto'
                                ? 'text-red-500'
                                : kind === 'flagged' ? 'text-red-400' : 'text-orange-400';
                            const title = kind === 'auto'
                                ? 'Auto-flagged'
                                : kind === 'flagged' ? 'Flagged for review' : 'Marked suspicious';
                            const chipCls = kind === 'auto'
                                ? 'bg-red-600/15 text-red-400'
                                : kind === 'flagged' ? 'bg-red-500/10 text-red-300' : 'bg-orange-500/10 text-orange-300';
                            return (
                                <div key={kind} className="mb-3">
                                    <div className="flex items-center justify-between px-2 py-1 text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                                        <span className="flex items-center gap-1.5">
                                            <Icon className={`w-3 h-3 ${iconCls}`} />
                                            {title} · {items.length}
                                        </span>
                                    </div>
                                    {items.map(m => (
                                        <div
                                            key={`${kind}-${m.id}`}
                                            className="flex items-center justify-between px-3 py-2.5 mb-1 hover:bg-muted/60 border border-transparent hover:border-border/50 rounded-lg transition-colors group"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs text-foreground font-mono truncate">
                                                    {safeStr(m.entity_name) || formatAddress(m.id)}
                                                </div>
                                                <div className="text-[10px] text-muted-foreground mt-0.5 flex gap-1.5 items-center flex-wrap">
                                                    <span className={`px-1 py-0.5 rounded text-[9px] uppercase font-bold ${chipCls}`}>
                                                        {kind}
                                                    </span>
                                                    {typeof m.risk_score === 'number' && m.risk_score > 0 ? (
                                                        <span>risk {Math.round(m.risk_score * 100)}%</span>
                                                    ) : null}
                                                    {kind === 'auto' && m.flag_reasons && m.flag_reasons.length > 0 && (
                                                        <span className="italic text-amber-500/80">{m.flag_reasons.map(r => r.replace(/_/g, ' ')).join(', ')}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => locateNode(m.id)}
                                                    title="Locate on canvas"
                                                    className="p-1.5 text-[10px] font-medium text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 rounded"
                                                >
                                                    <Crosshair className="w-3.5 h-3.5" />
                                                </button>
                                                {(kind === 'auto' || kind === 'flagged') && (
                                                    <button
                                                        onClick={() => confirmSuspicious(m.id)}
                                                        title={kind === 'auto'
                                                            ? 'Confirm as suspicious — promote system flag to officer verdict'
                                                            : 'Confirm as suspicious — promote review flag to officer verdict'}
                                                        className="p-1.5 text-[10px] font-medium text-red-500 bg-red-500/15 hover:bg-red-500/30 rounded"
                                                    >
                                                        <ShieldAlert className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => unmark(m.id, kind)}
                                                    title={kind === 'auto'
                                                        ? 'Dismiss system flag (re-fires on next triage if conditions still hold)'
                                                        : kind === 'flagged'
                                                            ? 'Dismiss review flag'
                                                            : 'Remove suspicious marker'}
                                                    className="p-1.5 text-[10px] font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            );
                        };
                        return (
                            <>
                                {renderSection(auto, 'auto')}
                                {renderSection(flagged, 'flagged')}
                                {renderSection(suspicious, 'suspicious')}
                            </>
                        );
                    })()}

                    {/* C5/F4 — "Reported" group: nodes with a generated report. */}
                    {reportedNodes.length > 0 && (
                        <div className="mb-3">
                            <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                                <FileText className="w-3 h-3 text-indigo-400" />
                                Reported · {reportedNodes.length}
                            </div>
                            {reportedNodes.map(r => (
                                <div
                                    key={`reported-${r.id}`}
                                    className="flex items-center justify-between px-3 py-2.5 mb-1 hover:bg-muted/60 border border-transparent hover:border-border/50 rounded-lg transition-colors group"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-foreground font-mono truncate">
                                            {safeStr(r.entity_name) || formatAddress(r.id)}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5 flex gap-1 items-center flex-wrap">
                                            {r.report_types.map(t => (
                                                <span key={t} className="px-1 py-0.5 rounded text-[9px] font-medium bg-muted text-foreground">
                                                    {REPORT_TYPE_LABEL[t] || t}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => locateNode(r.id)}
                                            title="Locate on canvas"
                                            className="p-1.5 text-[10px] font-medium text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 rounded"
                                        >
                                            <Crosshair className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {displayGroups.map(group => {
                        const items = group.items;
                        if (items.length === 0) return null;
                        return (
                            <div key={group.label} className="mb-3">
                                <div className="flex items-center justify-between px-2 py-1 text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                                    <span>{group.label} · {items.length}</span>
                                    <button
                                        onClick={() => restoreGroup(group)}
                                        className="text-[10px] text-emerald-500 hover:text-emerald-400 font-medium"
                                        title={`Restore all ${group.label.toLowerCase()}`}
                                    >
                                        Restore all
                                    </button>
                                </div>
                                {items.map((item) => {
                                    const el = item.element;
                                    const data = el.data as any;
                                    const isNode = item.group === 'nodes';
                                    const primary = isNode
                                        ? (safeStr(data.entity_name) || formatAddress(data.id))
                                        : `Edge: ${formatAddress(data.source)} → ${formatAddress(data.target)}`;
                                    return (
                                        <div
                                            key={`${data.id}-${item.stashedAt}`}
                                            className="flex items-center justify-between px-3 py-2.5 mb-1 hover:bg-muted/60 border border-transparent hover:border-border/50 rounded-lg transition-colors group"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs text-foreground font-mono truncate">{primary}</div>
                                                <div className="text-[10px] text-muted-foreground mt-0.5 flex gap-1.5 items-center">
                                                    <span className="px-1 py-0.5 bg-background rounded text-[9px] uppercase font-bold text-foreground/60">
                                                        {isNode ? 'Node' : 'Edge'}
                                                    </span>
                                                    {item.connectedEdgeIds?.length ? (
                                                        <span>+ {item.connectedEdgeIds.length} edge{item.connectedEdgeIds.length === 1 ? '' : 's'}</span>
                                                    ) : null}
                                                    <span>· {formatTime(item.stashedAt)}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => data.id && restoreOne(data.id)}
                                                className="ml-2 px-2.5 py-1 text-[10px] font-medium text-emerald-500 bg-emerald-500/10 hover:bg-emerald-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"
                                            >
                                                Restore
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>

                <div className="p-3 border-t border-border/60 flex gap-2 shrink-0 bg-muted/20">
                    <button
                        onClick={restoreEverything}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-500 hover:bg-emerald-500/10 rounded-md border border-border/50 transition-colors"
                    >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Restore All
                    </button>
                    <button
                        onClick={clearAll}
                        className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-500/10 rounded-md border border-border/50 transition-colors"
                        title="Discard All"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            {/* Pull Tab */}
            <div className="flex items-center pl-0">
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="h-20 w-8 bg-card border border-border border-l-0 rounded-r-lg flex flex-col items-center justify-center gap-2 hover:bg-muted transition-colors cursor-pointer group"
                    title="Toggle Stash Drawer"
                >
                    <Package className="w-4 h-4 text-muted-foreground group-hover:text-amber-500 transition-colors" />
                    {total > 0 && !expanded && (
                        <span className="text-[9px] font-bold text-amber-500 bg-amber-500/10 px-1 rounded block">{total}</span>
                    )}
                </button>
            </div>
        </div>
    );
};
